import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import { useDispatch } from 'store/hooks';

/**
 * Generate an Api Key for a specific bind.
 * @returns the api key.
 */
export const useGenerateApiKey = (
    bind: string,
    setApiKey: DispatchState<string>,
) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const response = await RQAPICallWithData<{
                apiKey: string;
            }>({
                endpoint: `support/${bind}/api_key`,
                method: 'POST',
            });

            return response.content.data?.apiKey;
        },

        onSuccess: (data: string) => {
            dispatch.vision.notifyUser({
                message: t('support.clientConfiguration.apiKey.success'),
                snackType: 'success',
            });
            setApiKey(data);
            queryClient.invalidateQueries({
                queryKey: ['apiKeys', bind],
            });
        },
    });
};

type ApiKeyInfos = {
    id: number;
    expirationDate: Date;
    isValid: boolean;
};

/**
 * Get the list of Api Key for a specific bind.
 * @returns the list of api keys.
 */
export const useGetApiKeyInfos = (bind: string) =>
    useQuery({
        queryKey: ['apiKeys', bind],

        queryFn: async () => {
            const response = await RQAPICallWithData<ApiKeyInfos[]>({
                endpoint: `support/${bind}/api_key`,
                method: 'GET',
            });

            return response.content.data;
        },
    });
